/**
 * Step 1 of the Add Manually wizard, as data: the procedures the carrier is
 * asked, each line's fields in groups that open one at a time from left to
 * right, and the helpers that fill a line from another (same rule, a shared
 * limit mirrored, the PMS's last-done date, the last breakdown).
 *
 * Nothing here talks to the server. A row's `edit` has the line drawer's
 * shape (LineEditDrawer `initialEdit`), so a saved line goes through the same
 * rules (lineEditError) and the same body (linePayload) as the drawer's.
 */
import dayjs from "dayjs";

import {
  LINE_CHOICES, PLAN_DETAIL_ROWS, choiceLabel, describeAgeTiers, lineEditError, parseCodeList, statusFromPercent,
} from "../../../lib/planDetails";
import { compareTableRows, procedureName } from "../../../lib/procedureFamilies";
import { SAME_RULE_GROUPS, initialEdit, linePayload } from "../sheet/LineEditDrawer";
import { SECTION_ORDER } from "../sheet/sheetModel";

export const isOrthoCode = (code) => /^(8|93)/.test(String(code || ""));

// The line's fields, in the order they open. Ortho terms only on ortho codes.
export const GROUPS = [
  { key: "covered", title: "Covered" },
  { key: "often", title: "How often" },
  { key: "ages", title: "Ages" },
  { key: "ortho", title: "Ortho terms", ortho: true },
  { key: "shared", title: "Shared limit" },
  { key: "history", title: "History" },
  { key: "note", title: "Note" },
];

export function groupsFor(code) {
  return GROUPS.filter((g) => !g.ortho || isOrthoCode(code));
}

// A coverage-row lookalike, so the tab's naming and order apply unchanged.
function asRow(code, name) {
  return { procedure_code_details: { procedure_code: code, description: name || "" } };
}

/** One procedure to ask, empty. */
export function entryRow(info) {
  const code = String(info.code || "").trim();
  const edit = initialEdit(asRow(code, info.name));
  edit.coverage_type = info.category || "";
  return {
    code,
    name: info.name || "",
    category: info.category || "",
    edit,
    done: {},          // group key -> true once ✓'d
    complete: false,   // every group ✓'d, or skipped
    skipped: false,
    filledBy: "",      // "Same rule as 01103", "From the last breakdown"
  };
}

/** The wizard's rows: the codes to ask, named and ordered as the tab orders them, by section. */
export function entryRows(codes) {
  const rows = (codes || []).filter((c) => c && c.code).map(entryRow);
  const section = (r) => {
    const i = SECTION_ORDER.indexOf(r.category);
    return i === -1 ? SECTION_ORDER.length : i;
  };
  return rows.sort((a, b) => section(a) - section(b) || compareTableRows(asRow(a.code, a.name), asRow(b.code, b.name)));
}

/** `{ name, qualifier }` as the tab names the procedure. */
export function nameOf(row) {
  const n = procedureName(asRow(row.code, row.name));
  return { name: n.name || row.name || row.code, qualifier: n.qualifier || "" };
}

// ---- the open group ---------------------------------------------------------

/** The first group of `row` not yet ✓'d, or null. */
export function firstOpenGroup(row) {
  const g = groupsFor(row.code).find((x) => !row.done[x.key]);
  return g ? g.key : null;
}

/** The next row (after `index`) still to finish, or -1. */
export function nextIncomplete(rows, index) {
  for (let i = index + 1; i < rows.length; i += 1) if (!rows[i].complete) return i;
  for (let i = 0; i <= index && i < rows.length; i += 1) if (!rows[i].complete) return i;
  return -1;
}

const replaceRow = (rows, index, row) => rows.map((r, i) => (i === index ? row : r));

/**
 * ✓ on the open group: it closes; the row's next group opens, or — after the
 * last — the row turns complete and the next unfinished row opens at its
 * first group. Returns `{ rows, cursor }`; cursor null when every row is done.
 */
export function confirmGroup(rows, cursor) {
  const row = rows[cursor.row];
  if (!row) return { rows, cursor: null };
  const done = Object.assign({}, row.done, { [cursor.group]: true });
  const groups = groupsFor(row.code);
  const next = groups.find((g) => !done[g.key]);
  const updated = Object.assign({}, row, { done, complete: !next });
  const out = replaceRow(rows, cursor.row, updated);
  if (next) return { rows: out, cursor: { row: cursor.row, group: next.key } };
  const i = nextIncomplete(out, cursor.row);
  return { rows: out, cursor: i === -1 ? null : { row: i, group: firstOpenGroup(out[i]) || groups[0].key } };
}

/** Skip: the row is saved empty (still to ask on the tab); the next row opens. */
export function skipRow(rows, index) {
  const row = rows[index];
  const blank = Object.assign({}, entryRow(row), { skipped: true, complete: true });
  const out = replaceRow(rows, index, blank);
  const i = nextIncomplete(out, index);
  return { rows: out, cursor: i === -1 ? null : { row: i, group: firstOpenGroup(out[i]) } };
}

/** Reopen a group of a row (any cell can be clicked). The row is no longer complete. */
export function reopen(rows, index, group) {
  const row = rows[index];
  const done = Object.assign({}, row.done);
  delete done[group];
  return {
    rows: replaceRow(rows, index, Object.assign({}, row, { done, complete: false, skipped: false })),
    cursor: { row: index, group },
  };
}

export function setField(rows, index, field, value) {
  const row = rows[index];
  return replaceRow(rows, index, Object.assign({}, row, {
    edit: Object.assign({}, row.edit, { [field]: value }), skipped: false,
  }));
}

export function removeRow(rows, index) {
  return rows.filter((r, i) => i !== index);
}

// ---- closed cells -------------------------------------------------------------

const blank = (v) => v == null || String(v).trim() === "";

/** One line saying what a closed group holds ("90% · Covered"), or "—". */
export function groupSummary(row, group, terms) {
  const e = row.edit;
  switch (group) {
    case "covered": {
      const status = statusFromPercent(e.percentage_covered, e.coverage_status);
      return [blank(e.percentage_covered) ? "" : `${e.percentage_covered}%`,
        status ? choiceLabel(LINE_CHOICES.coverage_status, status) : "",
        e.alternate_benefit === "yes" ? "alternate" : ""].filter(Boolean).join(" · ") || "—";
    }
    case "often": {
      if (e.isFrequencyUnlimited) return "No limit";
      const count = blank(e.max_occurrences) ? "" : e.max_occurrences;
      const months = blank(e.coverage_duration_months) ? "" : e.coverage_duration_months;
      const main = count && months ? `${count} / ${months} mo` : count ? `${count}×` : months ? `every ${months} mo` : "";
      const basis = e.frequency_basis ? choiceLabel(LINE_CHOICES.frequency_basis, e.frequency_basis).toLowerCase() : "";
      return [main, basis].filter(Boolean).join(" · ") || "—";
    }
    case "ages": {
      if (blank(e.age_min) && blank(e.age_max)) return "—";
      return describeAgeTiers([{ min_age: blank(e.age_min) ? null : Number(e.age_min), max_age: blank(e.age_max) ? null : Number(e.age_max) }]) || "—";
    }
    case "ortho": {
      const t = terms || {};
      return [t.ortho_payment ? choiceLabel(orthoPaymentOptions(), t.ortho_payment) : "",
        blank(t.ortho_initial_payment_pct) ? "" : `${t.ortho_initial_payment_pct}% initial`].filter(Boolean).join(" · ") || "—";
    }
    case "shared": {
      const codes = parseCodeList(e.shared_limit_codes_text).codes;
      if (!codes.length && blank(e.shared_limit_count)) return "—";
      const limit = !blank(e.shared_limit_count) ? `${e.shared_limit_count}${blank(e.shared_limit_months) ? "" : ` / ${e.shared_limit_months} mo`}` : "";
      return [codes.length ? `with ${codes.join(", ")}` : "", limit].filter(Boolean).join(" · ");
    }
    case "history":
      return [e.last_service_date ? `last ${e.last_service_date}` : "", e.eligible_again_on ? `next ${e.eligible_again_on}` : ""]
        .filter(Boolean).join(" · ") || "—";
    case "note":
      return blank(e.notes) ? "—" : String(e.notes).trim();
    default:
      return "—";
  }
}

export function orthoPaymentOptions() {
  return (PLAN_DETAIL_ROWS.find((r) => r.field === "ortho_payment") || {}).options || [];
}

/** Why the open row can't be ✓'d, or null (the drawer's rules). */
export function rowError(row) {
  return lineEditError(row.edit);
}

// ---- helpers that fill a row from another ------------------------------------

/** The other rows a carrier answers with this row's rule (01101/01102 after 01103), still untouched. */
export function sameRuleTargets(rows, index) {
  const row = rows[index];
  const group = SAME_RULE_GROUPS.find((g) => g.indexOf(row.code) !== -1);
  if (!group) return [];
  return rows
    .map((r, i) => ({ r, i }))
    .filter(({ r, i }) => i !== index && group.indexOf(r.code) !== -1 && !r.complete && !Object.keys(r.done).length)
    .map(({ i }) => i);
}

const RULE_FIELDS = [
  "percentage_covered", "coverage_status", "max_occurrences", "coverage_duration_months", "isFrequencyUnlimited",
  "frequency_basis", "limit_unit", "limit_scope", "age_min", "age_max",
  "shared_limit_codes_text", "shared_limit_count", "shared_limit_months",
];

/** Give `targets` the rule of row `index` (coverage, how often, ages, shared limit); they turn complete. */
export function applySameRule(rows, index, targets) {
  const from = rows[index];
  return rows.map((r, i) => {
    if (targets.indexOf(i) === -1) return r;
    const edit = Object.assign({}, r.edit);
    RULE_FIELDS.forEach((f) => { edit[f] = from.edit[f]; });
    edit.notes = `Same rule as ${from.code}.`;
    const done = groupsFor(r.code).reduce((o, g) => Object.assign(o, { [g.key]: true }), {});
    return Object.assign({}, r, { edit, done, complete: true, skipped: false, filledBy: `Same rule as ${from.code}` });
  });
}

/**
 * A limit shared between codes, said once: the codes on row `index` that
 * have a row with no shared limit yet get the same group (the row's own code
 * in place of theirs) and the same combined count. Returns the rows and the
 * codes filled.
 */
export function mirrorSharedLimit(rows, index) {
  const from = rows[index];
  const codes = parseCodeList(from.edit.shared_limit_codes_text).codes;
  if (!codes.length) return { rows, mirrored: [] };
  const mirrored = [];
  const out = rows.map((r, i) => {
    if (i === index || codes.indexOf(r.code) === -1) return r;
    if (!blank(r.edit.shared_limit_codes_text) || !blank(r.edit.shared_limit_count)) return r;
    const group = [from.code].concat(codes.filter((c) => c !== r.code));
    mirrored.push(r.code);
    return Object.assign({}, r, {
      edit: Object.assign({}, r.edit, {
        shared_limit_codes_text: group.join(", "),
        shared_limit_count: from.edit.shared_limit_count,
        shared_limit_months: from.edit.shared_limit_months,
      }),
      mirroredFrom: from.code,
    });
  });
  return { rows: out, mirrored };
}

/**
 * The next date a once-every-N-months rolling limit allows, from the last
 * done date: "" for anything else (several per period, a calendar or benefit
 * year, no date) — those depend on claims the PMS doesn't show.
 */
export function nextEligibleFrom(edit) {
  const e = edit || {};
  if (e.isFrequencyUnlimited || String(e.max_occurrences).trim() !== "1" || e.frequency_basis !== "rolling") return "";
  const months = parseInt(e.coverage_duration_months, 10);
  const last = dayjs(e.last_service_date, "YYYY-MM-DD");
  if (!months || Number.isNaN(months) || !e.last_service_date || !last.isValid()) return "";
  return last.add(months, "month").format("YYYY-MM-DD");
}

/**
 * Prefill from the last breakdown's lines (`previousRows`: coverage rows):
 * a row takes the values of the same code. Every row still needs its ✓s.
 * Codes the last breakdown asked that aren't listed are added.
 */
export function prefillFromPrevious(rows, previousRows) {
  const byCode = {};
  (previousRows || []).forEach((p) => {
    const det = (p && p.procedure_code_details) || {};
    const code = det.procedure_code != null ? String(det.procedure_code).trim() : "";
    if (code && !p.data_unavailable) byCode[code] = p;
  });
  const seen = {};
  const out = rows.map((r) => {
    seen[r.code] = true;
    const p = byCode[r.code];
    if (!p) return r;
    const edit = initialEdit(p);
    edit.procedure_code_id = null;
    edit.coverage_type = r.edit.coverage_type || edit.coverage_type;
    return Object.assign({}, r, { edit, done: {}, complete: false, skipped: false, filledBy: "From the last breakdown" });
  });
  Object.keys(byCode).forEach((code) => {
    if (seen[code]) return;
    const p = byCode[code];
    const det = p.procedure_code_details || {};
    const row = entryRow({ code, name: det.description || "", category: p.coverage_type || "" });
    row.edit = Object.assign(initialEdit(p), { procedure_code_id: null });
    row.filledBy = "From the last breakdown";
    out.push(row);
  });
  return out;
}

// ---- the save ---------------------------------------------------------------

/** The first row whose values can't be saved: `{ index, error }`, or null. */
export function firstInvalid(rows) {
  for (let i = 0; i < rows.length; i += 1) {
    const error = rows[i].skipped ? null : rowError(rows[i]);
    if (error) return { index: i, error };
  }
  return null;
}

/** The lines for breakdown-manual-create: `[{ code, ...line fields }]`. */
export function savePayload(rows) {
  return rows.map((r) => {
    if (r.skipped) return { code: r.code, coverage_type: r.edit.coverage_type || undefined };
    const body = linePayload(null, Object.assign({}, r.edit, { procedure_code_id: null })) || {};
    delete body.procedure_code;
    return Object.assign({ code: r.code }, body);
  });
}

/** Where the rows stand: `{ total, complete, skipped }`. */
export function progress(rows) {
  return {
    total: rows.length,
    complete: rows.filter((r) => r.complete).length,
    skipped: rows.filter((r) => r.skipped).length,
  };
}

// ---- the draft, kept until saved -------------------------------------------

const draftKey = (policyId) => `manualBreakdownDraft:${policyId}`;

export function loadDraft(policyId) {
  try {
    const raw = window.localStorage.getItem(draftKey(policyId));
    const d = raw ? JSON.parse(raw) : null;
    return d && Array.isArray(d.rows) && d.rows.length ? d : null;
  } catch (err) {
    return null;
  }
}

export function saveDraft(policyId, draft) {
  try {
    window.localStorage.setItem(draftKey(policyId), JSON.stringify(Object.assign({ savedAt: new Date().toISOString() }, draft)));
  } catch (err) {
    // Private windows and full storage: the wizard still works, without the draft.
  }
}

export function clearDraft(policyId) {
  try {
    window.localStorage.removeItem(draftKey(policyId));
  } catch (err) {
    // ignore
  }
}
